// ═══════════════════════════════════════════════════════════════
// PA Grants — FEMA 2025 Equipment Rate Schedule
// Read-only reference page; rate lookup for Force Account entry
// ═══════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import { Paper, Text, Group, Stack, Badge, TextInput, Select, Title } from '../../components/gab/GabComponents';
import { IconSearch, IconFilter } from '@tabler/icons-react';
import { FEMA_EQUIP_RATES, FEMA_CATEGORIES } from '../../data/femaEquipmentRates';
import type { FemaEquipRate } from '../../data/femaEquipmentRates';

const fmt = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  ...FEMA_CATEGORIES.map(c => ({ value: c, label: c })),
];

export default function PAEquipmentRates() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [sortField, setSortField] = useState<keyof FemaEquipRate>('costCode');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  function toggleSort(field: keyof FemaEquipRate) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return FEMA_EQUIP_RATES.filter(r => {
      const matchCat = !category || r.equipment.startsWith(category) ||
        FEMA_CATEGORIES.some(c => c === category && (r.equipment.toLowerCase().includes(c.toLowerCase())));
      const matchQ = !q ||
        r.costCode.toLowerCase().includes(q) ||
        r.equipment.toLowerCase().includes(q) ||
        r.manufacturer.toLowerCase().includes(q) ||
        r.specification.toLowerCase().includes(q) ||
        r.capacityOrSize.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [query, category]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const va = String(a[sortField] ?? '');
      const vb = String(b[sortField] ?? '');
      const n = va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? n : -n;
    });
  }, [filtered, sortField, sortDir]);

  function thStyle(col: keyof FemaEquipRate) {
    return {
      cursor: 'pointer', userSelect: 'none' as const,
      color: sortField === col ? 'var(--color-primary-700)' : '#64748b',
      whiteSpace: 'nowrap' as const,
    };
  }

  function indicator(col: keyof FemaEquipRate) {
    if (sortField !== col) return <span style={{ opacity: 0.25 }}>↑</span>;
    return sortDir === 'desc' ? '↓' : '↑';
  }

  return (
    <Stack gap={12}>
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Title order={3}>FEMA 2025 Equipment Rate Schedule</Title>
          <Text size="sm" c="dimmed">
            {FEMA_EQUIP_RATES.length} entries &mdash; Source: FEMA PA Schedule of Equipment Rates (2025)
          </Text>
        </Stack>
        <Badge color="var(--color-primary-700)" variant="light" size="lg">Reference Only</Badge>
      </Group>

      {/* Filters */}
      <Paper p="sm" withBorder>
        <Group gap="sm">
          <TextInput
            leftSection={<IconSearch size={14} />}
            placeholder="Search code, equipment, manufacturer, spec…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            style={{ flex: 1, minWidth: 200 }}
          />
          <Select
            leftSection={<IconFilter size={14} />}
            data={CATEGORY_OPTIONS}
            value={category}
            onChange={v => setCategory(v || '')}
            placeholder="All Categories"
            style={{ width: 220 }}
          />
          <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
            {sorted.length} result{sorted.length !== 1 ? 's' : ''}
          </Text>
        </Group>
      </Paper>

      {/* Table */}
      <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
            <tr>
              <th style={{ ...thStyle('costCode'), padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}
                onClick={() => toggleSort('costCode')}>
                Cost Code {indicator('costCode')}
              </th>
              <th style={{ ...thStyle('equipment'), padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}
                onClick={() => toggleSort('equipment')}>
                Equipment {indicator('equipment')}
              </th>
              <th style={{ ...thStyle('manufacturer'), padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}
                onClick={() => toggleSort('manufacturer')}>
                Manufacturer {indicator('manufacturer')}
              </th>
              <th style={{ ...thStyle('specification'), padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}
                onClick={() => toggleSort('specification')}>
                Specification {indicator('specification')}
              </th>
              <th style={{ ...thStyle('capacityOrSize'), padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}
                onClick={() => toggleSort('capacityOrSize')}>
                Capacity / Size {indicator('capacityOrSize')}
              </th>
              <th style={{ ...thStyle('hp'), padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}
                onClick={() => toggleSort('hp')}>
                HP {indicator('hp')}
              </th>
              <th style={{ ...thStyle('unit'), padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}
                onClick={() => toggleSort('unit')}>
                Unit {indicator('unit')}
              </th>
              <th style={{ ...thStyle('rate'), padding: '8px 10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}
                onClick={() => toggleSort('rate')}>
                Rate {indicator('rate')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr>
                <td colSpan={8} style={{ padding: '32px 10px', textAlign: 'center', color: '#94a3b8' }}>
                  No equipment rates match your search.
                </td>
              </tr>
            )}
            {sorted.map((r, i) => (
              <tr key={r.costCode + '-' + i}
                style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontWeight: 600, color: 'var(--color-primary-700)', whiteSpace: 'nowrap' }}>
                  {r.costCode}
                </td>
                <td style={{ padding: '7px 10px', fontWeight: 500 }}>{r.equipment}</td>
                <td style={{ padding: '7px 10px', color: '#475569' }}>{r.manufacturer || '—'}</td>
                <td style={{ padding: '7px 10px', color: '#475569' }}>{r.specification || '—'}</td>
                <td style={{ padding: '7px 10px', color: '#475569' }}>{r.capacityOrSize || '—'}</td>
                <td style={{ padding: '7px 10px', color: '#475569' }}>{r.hp || '—'}</td>
                <td style={{ padding: '7px 10px', color: '#475569' }}>{r.unit}</td>
                <td style={{ padding: '7px 10px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, color: '#1e293b' }}>
                  {fmt(r.rate)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Stack>
  );
}
